/**
 * LinkedRelationshipsPanel - Inspector panel showing linked governance artifacts
 * Tabs: Risks, Issues, Decisions, Dependencies with counts
 * Add/Unlink actions with RBAC checks
 * Features: Auto-scroll on open/tab change, internal list scrolling
 */

import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  AlertTriangle, AlertCircle, GitBranch, Link2, Plus, X, Eye,
  RefreshCw, ChevronRight, Shield, FileWarning, Clock, Search,
  CheckCircle, Circle, ArrowRight
} from 'lucide-react';
import { api } from '../api/client';
import { useAuth } from '../context/AuthContext';
import { useProductMode } from '../context/ProductModeContext';
import { trackEvent, TELEMETRY_EVENTS } from '../utils/telemetry';
import { toast } from 'sonner';

// Severity/Priority badges
const SEVERITY_COLORS = {
  critical: 'bg-red-100 text-red-700 border-red-200',
  high: 'bg-orange-100 text-orange-700 border-orange-200',
  medium: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  low: 'bg-green-100 text-green-700 border-green-200'
};

const STATUS_COLORS = {
  open: 'bg-blue-100 text-blue-700 border-blue-200',
  in_progress: 'bg-purple-100 text-purple-700 border-purple-200',
  approved: 'bg-green-100 text-green-700 border-green-200',
  rejected: 'bg-red-100 text-red-700 border-red-200',
  pending: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  blocked: 'bg-red-100 text-red-700 border-red-200',
  resolved: 'bg-gray-100 text-gray-700 border-gray-200'
};

const TAB_CONFIG = {
  risks: { icon: AlertTriangle, label: 'Risks', color: 'text-red-600' },
  issues: { icon: AlertCircle, label: 'Issues', color: 'text-orange-600' },
  decisions: { icon: GitBranch, label: 'Decisions', color: 'text-blue-600' },
  dependencies: { icon: Link2, label: 'Dependencies', color: 'text-purple-600' }
};

// Item row component
const LinkedItem = ({ item, type, canEdit, onView, onUnlink }) => {
  const [unlinking, setUnlinking] = useState(false);
  
  const getBadge = () => {
    if (type === 'risks' && item.severity) {
      return (
        <span className={`px-1.5 py-0.5 text-[10px] font-medium rounded border ${SEVERITY_COLORS[item.severity?.toLowerCase()] || SEVERITY_COLORS.medium}`}>
          {item.severity}
        </span>
      );
    }
    if (type === 'issues' && item.priority) {
      return (
        <span className={`px-1.5 py-0.5 text-[10px] font-medium rounded border ${SEVERITY_COLORS[item.priority?.toLowerCase()] || SEVERITY_COLORS.medium}`}>
          {item.priority}
        </span>
      );
    }
    if (type === 'decisions' && item.status) {
      return (
        <span className={`px-1.5 py-0.5 text-[10px] font-medium rounded border ${STATUS_COLORS[item.status?.toLowerCase()] || STATUS_COLORS.pending}`}>
          {item.status}
        </span>
      );
    }
    if (type === 'dependencies' && item.state) {
      return (
        <span className={`px-1.5 py-0.5 text-[10px] font-medium rounded border ${STATUS_COLORS[item.state?.toLowerCase()] || STATUS_COLORS.pending}`}>
          {item.state}
        </span>
      );
    }
    return null;
  };
  
  const handleUnlink = async () => {
    setUnlinking(true);
    try {
      await onUnlink(item.id);
    } finally {
      setUnlinking(false);
    }
  };
  
  return (
    <div className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded-lg group" data-testid={`linked-item-${item.id}`}>
      {getBadge()}
      <span className="flex-1 text-sm text-gray-800 truncate" title={item.title}>
        {item.title}
      </span>
      {item.updatedAt && (
        <span className="text-[10px] text-gray-400 hidden group-hover:inline">
          {new Date(item.updatedAt).toLocaleDateString()}
        </span>
      )}
      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <button
          onClick={() => onView?.(item)}
          className="p-1 text-gray-400 hover:text-blue-600 rounded"
          title="View details"
        >
          <Eye className="w-3.5 h-3.5" />
        </button>
        {canEdit && (
          <button
            onClick={handleUnlink}
            disabled={unlinking}
            className="p-1 text-gray-400 hover:text-red-600 rounded disabled:opacity-50"
            title="Unlink"
          >
            {unlinking ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <X className="w-3.5 h-3.5" />}
          </button>
        )}
      </div>
    </div>
  );
};

// Main Panel Component
const LinkedRelationshipsPanel = ({
  workId,
  workType,
  entityType,
  onViewArtifact,
  onOpenSelector,
  initialTab = 'risks',  // Support opening to specific tab
  autoScrollEnabled = true  // Enable auto-scroll behavior
}) => {
  const { user } = useAuth();
  const { getFeature } = useProductMode();
  
  const tenantId = user?.tenant?.id || user?.tenant_id;
  
  const [activeTab, setActiveTab] = useState(initialTab);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ risks: [], issues: [], decisions: [], dependencies: [] });
  const [includeChildren, setIncludeChildren] = useState(false);
  const [error, setError] = useState(null);
  
  // Refs for auto-scroll
  const panelRef = useRef(null);
  const lastUserScrollRef = useRef(0);
  const SCROLL_DEBOUNCE_MS = 2000; // Respect user scroll for 2 seconds
  
  // Check if governance relationships feature is enabled
  const featureEnabled = useMemo(() => {
    if (typeof getFeature !== 'function') return false;
    return getFeature('governance.relationships', false);
  }, [getFeature]);
  
  // Check if user can edit (governance editor)
  const canEdit = useMemo(() => {
    const role = user?.role?.toLowerCase() || '';
    return ['admin', 'pmo', 'program_manager', 'project_manager', 'portfolio_manager'].includes(role);
  }, [user?.role]);
  
  // Check if this is a parent node (can show includeChildren toggle)
  const isParentNode = useMemo(() => {
    return ['portfolio', 'program', 'project', 'epic', 'feature'].includes(workType?.toLowerCase());
  }, [workType]);
  
  // Auto-scroll panel into view
  const scrollPanelIntoView = useCallback(() => {
    if (!autoScrollEnabled || !panelRef.current) return;
    
    // Check if user has scrolled recently
    const now = Date.now();
    if (now - lastUserScrollRef.current < SCROLL_DEBOUNCE_MS) {
      return;
    }
    
    // Smooth scroll panel header into view
    panelRef.current.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
      inline: 'nearest'
    });
    
    // Track telemetry
    trackEvent(TELEMETRY_EVENTS.INSPECTOR_LINKED_PANEL_AUTOSCROLL, {
      workId,
      tab: activeTab
    });
  }, [autoScrollEnabled, activeTab, workId]);
  
  // Handle user scroll to respect their scrolling
  useEffect(() => {
    const handleScroll = () => {
      lastUserScrollRef.current = Date.now();
    };
    
    // Listen to scroll on the drawer container
    const drawer = panelRef.current?.closest('[data-testid="detail-drawer-content"]');
    if (drawer) {
      drawer.addEventListener('scroll', handleScroll, { passive: true });
      return () => drawer.removeEventListener('scroll', handleScroll);
    }
  }, []);
  
  // Auto-scroll on initial mount
  useEffect(() => {
    if (featureEnabled && workId) {
      // Delay slightly to ensure DOM is ready
      const timer = setTimeout(scrollPanelIntoView, 100);
      return () => clearTimeout(timer);
    }
  }, [featureEnabled, workId]);
  
  // Auto-scroll on tab change
  useEffect(() => {
    if (featureEnabled && workId && activeTab) {
      scrollPanelIntoView();
    }
  }, [activeTab, scrollPanelIntoView]);
  
  useEffect(() => {
    if (featureEnabled && workId && tenantId) {
      loadLinkedData();
    } else {
      setLoading(false);
    }
  }, [workId, tenantId, includeChildren, featureEnabled]);
  
  const loadLinkedData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await api.getGovernanceByWork(workId, tenantId, includeChildren);
      setData(response.data);
      
      // Track tab open
      trackEvent(TELEMETRY_EVENTS.INSPECTOR_LINKED_TAB_OPENED, {
        tab: activeTab,
        workId
      });
    } catch (err) {
      console.error('Failed to load linked relationships:', err);
      setError(err.response?.data?.detail || 'Failed to load linked data');
    } finally {
      setLoading(false);
    }
  };
  
  const handleUnlink = async (artifactId) => {
    const artifactType = activeTab === 'dependencies' ? 'dependency' : activeTab.slice(0, -1);
    
    try {
      await api.deleteGovernanceLinks({
        artifactType,
        artifactId,
        workIds: [workId]
      }, tenantId, user?.id);
      
      // Track telemetry
      trackEvent(TELEMETRY_EVENTS.GOV_LINK_REMOVED, {
        type: artifactType,
        artifactId,
        workId
      });
      
      toast.success('Link removed');
      loadLinkedData();
    } catch (err) {
      toast.error('Failed to remove link');
    }
  };
  
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    trackEvent(TELEMETRY_EVENTS.INSPECTOR_LINKED_TAB_OPENED, {
      tab,
      workId
    });
  };
  
  const handleAddLink = () => {
    onOpenSelector?.({
      workId,
      workType,
      onLinkCreated: loadLinkedData
    });
  };
  
  // Feature not enabled
  if (!featureEnabled) {
    return null;
  }
  
  // Get counts
  const counts = {
    risks: data.risks?.length || 0,
    issues: data.issues?.length || 0,
    decisions: data.decisions?.length || 0,
    dependencies: data.dependencies?.length || 0
  };
  
  const totalCount = counts.risks + counts.issues + counts.decisions + counts.dependencies;
  
  return (
    <div 
      ref={panelRef}
      className="border-t border-gray-200 pt-4 mt-4" 
      data-testid="linked-relationships-panel"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Link2 className="w-4 h-4 text-gray-500" />
          <span className="text-sm font-medium text-gray-800">Linked Relationships</span>
          {totalCount > 0 && (
            <span className="px-1.5 py-0.5 text-[10px] font-medium bg-purple-100 text-purple-700 rounded">
              {totalCount}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {/* Include children toggle for parent nodes */}
          {isParentNode && (
            <button
              onClick={() => setIncludeChildren(!includeChildren)}
              className={`text-[10px] px-2 py-1 rounded-full border transition-colors ${
                includeChildren
                  ? 'bg-purple-50 border-purple-300 text-purple-700'
                  : 'bg-gray-50 border-gray-200 text-gray-500 hover:border-purple-300'
              }`}
              title={includeChildren ? 'Showing links from all descendants' : 'Showing direct links only'}
            >
              {includeChildren ? 'Including children' : 'Direct links'}
            </button>
          )}
          
          {/* Add link button */}
          {canEdit && (
            <button
              onClick={handleAddLink}
              className="flex items-center gap-1 text-xs text-purple-600 hover:text-purple-700 px-2 py-1 rounded-lg hover:bg-purple-50"
              data-testid="add-link-btn"
            >
              <Plus className="w-3.5 h-3.5" />
              Add
            </button>
          )}
        </div>
      </div>
      
      {/* Tabs */}
      <div className="flex gap-1 mb-3 overflow-x-auto">
        {Object.entries(TAB_CONFIG).map(([key, config]) => (
          <button
            key={key}
            onClick={() => handleTabChange(key)}
            className={`flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-lg whitespace-nowrap transition-colors ${
              activeTab === key
                ? 'bg-gray-900 text-white'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
            data-testid={`linked-tab-${key}`}
          >
            <config.icon className={`w-3.5 h-3.5 ${activeTab === key ? 'text-white' : config.color}`} />
            {config.label}
            {counts[key] > 0 && (
              <span className={`px-1.5 py-0.5 text-[10px] rounded-full ${
                activeTab === key ? 'bg-white/20' : 'bg-gray-200'
              }`}>
                {counts[key]}
              </span>
            )}
          </button>
        ))}
      </div>
      
      {/* Content - Fixed height with internal scrolling */}
      <div className="min-h-[80px] max-h-[300px] overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-4">
            <RefreshCw className="w-5 h-5 text-gray-400 animate-spin" />
          </div>
        ) : error ? (
          <div className="text-center py-4 text-sm text-red-600">
            {error}
          </div>
        ) : data[activeTab]?.length === 0 ? (
          <div className="text-center py-6">
            <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-2">
              {React.createElement(TAB_CONFIG[activeTab].icon, { 
                className: `w-5 h-5 ${TAB_CONFIG[activeTab].color}` 
              })}
            </div>
            <p className="text-sm text-gray-500">
              No linked {activeTab} yet.
            </p>
            {canEdit && (
              <button
                onClick={handleAddLink}
                className="mt-2 text-xs text-purple-600 hover:text-purple-700"
              >
                Add one to reflect impact →
              </button>
            )}
            {!canEdit && (
              <p className="mt-2 text-[10px] text-gray-400">
                You don't have permission to modify links.
              </p>
            )}
          </div>
        ) : (
          <div 
            className="space-y-1 max-h-[250px] overflow-y-auto pr-1 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent"
            data-testid={`linked-items-list-${activeTab}`}
          >
            {data[activeTab].map(item => (
              <LinkedItem
                key={item.id}
                item={item}
                type={activeTab}
                canEdit={canEdit}
                onView={onViewArtifact}
                onUnlink={handleUnlink}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default LinkedRelationshipsPanel;
